/**
 * Mirror of backend/src/utils/page-catalog.ts. Keep in sync.
 *
 * `key` is the stable identifier persisted in User.allowedPages.
 * `routes` is the set of frontend paths this catalog entry covers — the
 * sidebar and PermittedRoute guard intersect these against user.allowedPages.
 */
/** Which domain-scoped product view a page belongs to:
 *   'spares'  — parts & procurement (spares.crystalgrp.xyz)
 *   'service' — field service / job orders (service.crystalgrp.xyz)
 *   'both'    — shown on both spares. and service.
 *   'ims'     — IMS Containers realm (ims.crystalgrp.xyz), its own layout
 *  Untagged entries are treated as 'both'. */
export type PageProduct = 'spares' | 'service' | 'both' | 'ims';

export interface PageEntry {
  key: string;
  label: string;
  routes: string[];
  product?: PageProduct;
}

export const PAGE_CATALOG: ReadonlyArray<PageEntry> = [
  { key: 'dashboard',       label: 'Dashboard',                      routes: ['/'],                                   product: 'both' },
  { key: 'indents',         label: 'Spare Part Requisition',         routes: ['/indents', '/requisitions', '/material-req'], product: 'spares' },
  { key: 'scan',            label: 'Scan IN/OUT',                    routes: ['/scan'],                               product: 'spares' },
  { key: 'jo-tracker',      label: 'Job Order Tracker',              routes: ['/jo-tracker', '/jo-tat-analytics'],    product: 'service' },
  { key: 'container-service-history', label: 'Container History',     routes: ['/container-service-history'],           product: 'service' },
  { key: 'pm-schedule',     label: 'PM Schedule',                    routes: ['/pm-schedule'],                        product: 'service' },
  { key: 'data-actions',    label: 'Needs Confirmation',             routes: ['/data-actions'],                       product: 'service' },
  { key: 'service-monday-report', label: 'Monday Meeting',           routes: ['/service-monday-report'],              product: 'service' },
  { key: 'live-status',     label: 'Live Status',                    routes: ['/live-status'],                        product: 'service' },
  { key: 'job-cost-review', label: 'Job Cost Review',                routes: ['/job-cost-review'],                    product: 'service' },
  { key: 'efficiency',      label: 'Efficiency',                     routes: ['/efficiency'],                         product: 'both' },
  { key: 'technicians',     label: 'Technicians',                    routes: ['/technicians'],                        product: 'service' },
  { key: 'deliveries',      label: 'Deliveries',                     routes: ['/deliveries'],                         product: 'service' },
  { key: 'partcodes',       label: 'Part Codes',                     routes: ['/partcodes', '/p'],                    product: 'spares' },
  { key: 'transactions',    label: 'Transactions',                   routes: ['/transactions'],                       product: 'spares' },
  { key: 'bills',           label: 'Bills',                          routes: ['/bills'],                              product: 'spares' },
  { key: 'accounts',        label: 'Accounts (Purchases)',           routes: ['/accounts', '/accounts/purchases'],    product: 'spares' },
  { key: 'procurement',     label: 'POs / GRNs / Issues / On-Site',  routes: ['/pos', '/grns', '/issue-notes', '/onsite', '/old-part-dispositions'], product: 'spares' },
  { key: 'vendors',         label: 'Vendors',                        routes: ['/vendors'],                            product: 'spares' },
  { key: 'vendor-coord',    label: 'Vendor Coordination',            routes: ['/vendor-coord'],                       product: 'spares' },
  { key: 'masters',         label: 'Masters & SKU Requests',         routes: ['/masters', '/partcode-requests'],      product: 'spares' },
  { key: 'approval-rules',  label: 'Approval Rules & Audit',         routes: ['/approval-rules', '/audit'],           product: 'both' },
  // The four report pages live behind one hub route (/reports, Reports &
  // Analytics). The keys stay separate so per-user grants stay granular — the
  // hub only renders the tabs a user's keys cover — but every key must open the
  // hub route, hence '/reports' on all four.
  { key: 'reports',         label: 'Reports',                        routes: ['/reports'],                            product: 'both' },
  { key: 'service-report',  label: 'Service Report',                 routes: ['/reports', '/service-report'],         product: 'service' },
  { key: 'service-analytics', label: 'Service Analytics',            routes: ['/reports', '/analytics/service'],      product: 'service' },
  { key: 'recurring-chargeable', label: 'Recurring / Chargeable',    routes: ['/reports', '/recurring-chargeable'],   product: 'spares' },
  { key: 'ims-containers',  label: 'IMS Containers',                 routes: ['/ims-containers'],                     product: 'ims' },
  // Per-tab IMS catalog entries — each matches a sidebar item in
  // ContainersLayout. Granular per-user grants flow through these on the
  // IMS-side Page Access grid. The sidebar filter in ContainersLayout
  // honours allowedPages against these keys (with `ims-containers` as a
  // top-level "grant all IMS tabs" shortcut for backward compat).
  // LABELS MUST MATCH THE SIDEBAR. An admin granting access looks for the name
  // they see in the rail ("Movements", "Suppliers", "Sitting time") — when this
  // list said "Transactions"/"Vendors"/"Aging" instead, those pages looked
  // absent and couldn't be granted. `key` is what's persisted in
  // User.allowedPages, so keys never change; only these labels do.
  { key: 'ims:live-stock',   label: 'Live Stock',                     routes: ['/ims/live-stock'],   product: 'ims' },
  { key: 'ims:finder',       label: 'Find a container',               routes: ['/ims/finder'],       product: 'ims' },
  { key: 'ims:overview',     label: 'Overview',                       routes: ['/ims/overview'],     product: 'ims' },
  { key: 'ims:on-lease',     label: 'On Lease',                       routes: ['/ims/on-lease'],     product: 'ims' },
  { key: 'ims:sold',         label: 'Sold',                           routes: ['/ims/sold'],         product: 'ims' },
  { key: 'ims:off-lease',    label: 'Returned',                       routes: ['/ims/off-lease'],    product: 'ims' },
  { key: 'ims:transactions', label: 'Movements',                      routes: ['/ims/transactions'], product: 'ims' },
  { key: 'ims:exchange',     label: 'Exchange of Machine',            routes: ['/ims/exchange'],     product: 'ims' },
  { key: 'service-requests', label: 'Service Requests',                routes: ['/service-requests'],  product: 'service' },
  { key: 'ims:financials',   label: 'Money',                          routes: ['/ims/financials'],   product: 'ims' },
  { key: 'ims:vendors',      label: 'Suppliers',                      routes: ['/ims/vendors'],      product: 'ims' },
  { key: 'ims:exceptions',   label: 'Things to fix',                  routes: ['/ims/exceptions'],   product: 'ims' },
  // Was MISSING from this file while the backend had it — so the Verification
  // page could never be granted to a non-admin. (The two catalogs must mirror.)
  { key: 'ims:reconciliation', label: 'Verification',                 routes: ['/ims/reconciliation'], product: 'ims' },
  { key: 'ims:aging',        label: 'Sitting time',                   routes: ['/ims/aging'],        product: 'ims' },
  { key: 'ims:stock-health', label: 'Stock Health',                   routes: ['/ims/stock-health'], product: 'ims' },
  { key: 'ims:analytics',    label: 'Container Analytics',            routes: ['/ims/analytics'],    product: 'ims' },
  // Procurement-only — kept as a category key for the container-acl mask
  // service (hideProcurement) even though there's no dedicated sidebar
  // tab; the procurement data lives embedded in other pages.
  { key: 'ims:procurement',  label: 'Procurement (embedded panels)',  routes: ['/ims/procurement-only'], product: 'ims' },
  { key: 'rfqs',            label: 'RFQs',                           routes: ['/rfqs'],                               product: 'spares' },
  { key: 'petty-cash',      label: 'Petty Cash',                     routes: ['/petty-cash', '/petty-cash-approval-rules'], product: 'service' },
  { key: 'containers',      label: 'Containers',                     routes: ['/containers'],                         product: 'spares' },
  { key: 'travel-planning', label: 'Travel Planning',                routes: ['/travel-planning'],                    product: 'service' },
  { key: 'ticket-request',  label: 'Ticket Request',                 routes: ['/travel-planning'],                    product: 'service' },
  // Crystal Public API v1. `api-keys` is additionally admin-gated (sidebar row,
  // the AdminOnly route guard, and requireRole('admin') on the backend router) —
  // the catalog key exists so the page appears in the Page Access grid like
  // every other one. `integration-kit` is read-only developer documentation.
  { key: 'api-keys',        label: 'API Keys',                       routes: ['/api-keys'],                           product: 'both' },
  { key: 'integration-kit', label: 'Integration Kit (API docs)',     routes: ['/integration-kit'],                    product: 'both' },
];

export const PAGE_KEYS: ReadonlyArray<string> = PAGE_CATALOG.map((p) => p.key);

/**
 * Pages that legacy "all access" does NOT cover.
 *
 * Most accounts predate per-page grants and carry `allowedPages == null`, which
 * the route guard reads as "every page". A new page therefore becomes visible
 * to EVERYONE the moment it is added to the catalog — the opposite of
 * restricting it. Keys listed here need an admin, or an explicit grant that
 * actually contains the key; a null grant is not enough.
 *
 * The server enforces the same rule on /api/efficiency — this only keeps the
 * page out of the sidebar and off the route.
 */
export const RESTRICTED_PAGE_KEYS: ReadonlyArray<string> = ['efficiency'];

/** True when this path belongs to a restricted page. */
export function isRestrictedPath(pathname: string): boolean {
  for (const e of PAGE_CATALOG) {
    if (!RESTRICTED_PAGE_KEYS.includes(e.key)) continue;
    for (const r of e.routes) {
      if (pathname === r || (r !== '/' && pathname.startsWith(r + '/'))) return true;
    }
  }
  return false;
}

/** May this user reach a restricted page? Admin/master, or an explicit grant. */
export function mayUseRestricted(
  user: { role?: string; master?: boolean; allowedPages?: string[] | null } | null | undefined,
  key: string,
): boolean {
  if (!user) return false;
  if (user.role === 'admin' || user.master === true) return true;
  return Array.isArray(user.allowedPages) && user.allowedPages.includes(key);
}

/**
 * Resolve a user's allowedPages (array of keys) to the set of route paths
 * they're allowed to visit. Admins skip the check entirely — callers
 * should short-circuit for role==='admin' before calling this.
 */
export function pathsForKeys(keys: string[] | undefined | null): Set<string> {
  if (!keys) return new Set();
  const out = new Set<string>();
  for (const e of PAGE_CATALOG) {
    if (keys.includes(e.key)) for (const r of e.routes) out.add(r);
  }
  return out;
}

/** True when `pathname` is permitted under the given allowedPages keys.
 *  Matches by exact route OR prefix (`pathname === route || startsWith(route + '/')`)
 *  so `/p/CPC-S-1017` still resolves under the `/p` route in `partcodes`. */
export function isPathAllowed(pathname: string, allowedKeys: string[] | undefined | null): boolean {
  const paths = pathsForKeys(allowedKeys);
  for (const r of paths) {
    if (pathname === r) return true;
    if (r !== '/' && (pathname === r || pathname.startsWith(r + '/'))) return true;
  }
  return false;
}

/* ── Domain-scoped product views (spares. / service. / ims.) ─────────────── */
export type Product = 'spares' | 'service' | 'ims';

/**
 * The product view for the current page load, from the login domain:
 *   spares.*  → 'spares' · service.* → 'service' · ims.* → 'ims'
 * A `?product=` query (persisted in sessionStorage) overrides for local dev;
 * localhost / any other host returns null = NO scoping (every page shows).
 */
export function currentProduct(): Product | null {
  if (typeof window === 'undefined') return null;
  try {
    const q = new URLSearchParams(window.location.search).get('product');
    if (q === 'spares' || q === 'service' || q === 'ims') { sessionStorage.setItem('cg-product', q); return q; }
    const s = sessionStorage.getItem('cg-product');
    if (s === 'spares' || s === 'service' || s === 'ims') return s;
  } catch { /* ignore */ }
  const h = window.location.hostname.toLowerCase();
  if (h.startsWith('ims.')) return 'ims';
  if (h.startsWith('service.')) return 'service';
  if (h.startsWith('spares.')) return 'spares';
  return null;
}

/** Does a page (by its product tag) belong under the active product view?
 *  `product === null` (dev / unscoped) shows everything. */
export function pageMatchesProduct(entryProduct: PageProduct | undefined, product: Product | null): boolean {
  if (product === null) return true;
  const p = entryProduct ?? 'both';
  if (product === 'ims') return p === 'ims';
  return p === product || p === 'both'; // spares/service domains never show 'ims' pages
}

/** True when a catalog KEY is visible under the active product view. */
export function keyMatchesProduct(key: string, product: Product | null): boolean {
  if (product === null) return true;
  const e = PAGE_CATALOG.find((x) => x.key === key);
  return pageMatchesProduct(e?.product, product);
}

/** True when a PATH is reachable under the active product view. Any catalog
 *  entry covering the path that matches the product opens it; a path not in the
 *  catalog (e.g. /settings) is never blocked here. */
export function pathMatchesProduct(pathname: string, product: Product | null): boolean {
  if (product === null) return true;
  const matching = PAGE_CATALOG.filter((e) =>
    e.routes.some((r) => pathname === r || (r !== '/' && pathname.startsWith(r + '/'))));
  if (!matching.length) return true;
  return matching.some((e) => pageMatchesProduct(e.product, product));
}
